import {
  ComputeHiddenPlaceholdersState,
  useClipFrustum,
  useViewportRef,
} from "@faro-lotv/app-component-toolbox";
import { pixels2m } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { RefObject, useCallback, useRef } from "react";
import { Group, Matrix4, Plane, Vector3 } from "three";

/** Temporary vector for math computations */
const WORLD_SCALE = new Vector3();

type SubSamplePlaceholdersReturn = {
  /** The function used to filer out a set of points */
  computeHiddenPlaceholders(state: ComputeHiddenPlaceholdersState): number[];
  /** The group of which the placeholders renderer is child */
  groupRef: RefObject<Group | null>;
};

/**
 * Return a callback to filter a set of placeholders rendered as points
 *
 * @param references The list of extra positions the placeholders should not overlap with
 * @param pixelSize The size in pixels of the points
 * @param locked The list of locked positions
 * @param clippingPlanes Optional clipping planes. If provided, points outside the frustum will be hidden.
 * @param excluded A flag per point that, when set, force-hides it and excludes it from the sub-sampling
 * @returns The group reference and the filtering function
 */
export function useSubSamplePlaceholders(
  references: Vector3[],
  pixelSize: number,
  locked: boolean[] = [],
  clippingPlanes?: Plane[],
  excluded: boolean[] = [],
): SubSamplePlaceholdersReturn {
  const camera = useThree((state) => state.camera);
  const viewport = useViewportRef();

  const groupRef = useRef<Group>(null);
  const visiblePoints = useRef<Vector3[]>([]);
  const lastThreshold = useRef<number>(undefined);
  const lastExcluded = useRef<boolean[]>(excluded);
  const lastReferences = useRef<Vector3[]>(references);

  const clipFrustum = useClipFrustum(clippingPlanes);

  // Computes the hidden placeholders based on their screen-space distance
  // Prioritizes showing the reference positions (start and end placeholders)
  const computeHiddenPlaceholders = useCallback(
    ({
      positions,
      previousHidden,
      havePointsChanged,
    }: ComputeHiddenPlaceholdersState) => {
      if (!viewport.current) return [];

      const scale = groupRef.current?.getWorldScale(WORLD_SCALE).x ?? 1;
      const matrixWorld = groupRef.current?.matrixWorld ?? new Matrix4();
      // Min distance between two points, taking into account the world scale applied to the
      // entire point group (assuming it's uniform)
      let threshold = pixels2m(
        (pixelSize * 2) / scale,
        camera,
        viewport.current.height,
        camera.position.y,
      );

      // Quantize in exponential space
      threshold = Math.log2(threshold);
      threshold = Math.round(threshold);
      threshold = Math.pow(2, threshold);

      // Calculate square to optimize distance query
      const thresholdSq = threshold * threshold;

      if (
        lastThreshold.current === thresholdSq &&
        !havePointsChanged &&
        lastExcluded.current === excluded &&
        lastReferences.current === references
      ) {
        return previousHidden;
      }
      lastThreshold.current = thresholdSq;
      lastExcluded.current = excluded;
      lastReferences.current = references;

      const tempWorldPosition = new Vector3();

      const hidden = [];
      visiblePoints.current.length = 0;
      for (let idx = 0; idx < positions.length; ++idx) {
        // Force-hidden points neither render nor reserve screen space for the sub-sampling.
        if (excluded[idx]) {
          hidden.push(idx);
          continue;
        }
        tempWorldPosition.copy(positions[idx]).applyMatrix4(matrixWorld);
        const isInFrustum =
          !clipFrustum || clipFrustum.containsPoint(tempWorldPosition);

        const isVisible =
          isInFrustum &&
          (locked[idx] ||
            (!references.some(
              (p) => positions[idx].distanceToSquared(p) < thresholdSq,
            ) &&
              !visiblePoints.current.some(
                (point) =>
                  point.distanceToSquared(positions[idx]) < thresholdSq,
              )));
        if (isVisible) {
          visiblePoints.current.push(positions[idx]);
        } else {
          hidden.push(idx);
        }
      }
      return hidden;
    },
    [camera, clipFrustum, locked, excluded, pixelSize, references, viewport],
  );

  return { groupRef, computeHiddenPlaceholders };
}
